import React, { useCallback, useRef } from 'react';
import { DropDownButton } from 'devextreme-react';
import parseUrl from '#/common/utils/parsers/parseUrl';
import { useViewport } from '@nuvolo/nuux-theme/hooks/useViewport';
import type { EditorProps } from './EditorProps';
import If from '../../If';
import styled from 'styled-components';
import { color } from '@nuvolo/nuux-theme/utils';
import { borderRadius } from '#/common/hooks/useClientTheme';

export const StyledDropDownButton = styled(DropDownButton)`
  height: 44px !important;
  width: 100% !important;
  border-radius: ${borderRadius?.s || '16px'} !important;
  background-color: ${color('ui', 'light2')};
  margin: 0 !important;
  padding: 0 10px !important;
  margin-top: 25px !important;
  border: none !important;

  div:first-child {
    background-color: transparent !important;
    border-radius: unset !important;
    border: none !important;
    opacity: 1;
  }

  .dx-button-text {
    text-transform: none !important;
  }

  .dx-button-has-icon {
    border: none !important;
  }

  .dx-button-mode-outlined.dx-state-disabled .dx-button-text {
    color: ${color('ui', 'dark3')} !important;
  }
`;

interface SplitButtonOptions {
  title: string;
  baseUrl: string;
  addFieldValueToUrl?: string; // field name to append value to url, e.g. /items/{value}
  disabled?: boolean | ((record: any) => boolean);
}

interface SplitButtonsOptions {
  buttons?: SplitButtonOptions[];
  mainBtnTitle?: string; // if multiple buttons, show this title on the main button
  desktopOnly?: boolean;
  readOnly?: boolean;
  hide?: boolean;
}

export interface SplitButtonProps {
  editorType: 'SplitButton';
  editorOptions: SplitButtonsOptions;
}

export type { SplitButtonsOptions };

const SplitButton: React.FC<EditorProps<SplitButtonsOptions>> = ({ editorOptions, form }) => {
  const { buttons, mainBtnTitle, desktopOnly, readOnly, hide } = editorOptions;

  const readonly = form.option('readOnly') || readOnly;

  const { isDesktop } = useViewport();
  const showButton = !hide && (!desktopOnly || isDesktop);

  const dropDownRef = useRef<DropDownButton>(null);

  const formData = form.option('formData');

  const resolvedButtons = buttons?.map((btn) => ({
    ...btn,
    disabled: typeof btn.disabled === 'function' ? btn.disabled(formData) : btn.disabled,
  }));

  const onSplitButtonClick = useCallback(() => {
    dropDownRef.current?.instance.open();
  }, []);

  const onItemClick = (e: any) => {
    const data = e.itemData as SplitButtonOptions;
    if (data.disabled) return;
    // Get the value from the form based on addFieldValueToUrl, and then navigate to the url
    const redirectUrl = data.addFieldValueToUrl
      ? parseUrl(data.baseUrl, {
          value: encodeURIComponent(
            data.addFieldValueToUrl
              .split('.')
              .reduce((acc: any, key: string) => acc?.[key], formData),
          ), // Supports nested fields like model.name
        })
      : data.baseUrl;
    window.open(redirectUrl, '_blank');
  };

  return (
    <If condition={showButton}>
      <StyledDropDownButton
        ref={dropDownRef}
        disabled={readonly}
        text={mainBtnTitle}
        // icon="save" // Set this dummy icon, then replace it using the split-button-trane-icon class in GlobalStyles.ts
        items={resolvedButtons}
        // TODO when enough time: itemRender - make it with icon="arrow-right"
        splitButton={true}
        onButtonClick={onSplitButtonClick}
        onItemClick={onItemClick}
        displayExpr="title"
        // In order to add the trane icon - uncomment the icon prop above and add the split-button-trane-icon class below
        className="cards-dropdown-select"
      />
    </If>
  );
};

export default React.memo(SplitButton);
